import { FlagsModule } from '@seddi/ui-feature-flags';
import { type FC, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

import { AppLoadingPage } from '@/components/AppLoadingPage';
import { API_ADDRESS, DEPLOYMENT_ENV, GITLAB_UNLEASH_CONNECT_SRC, NODE_ENV } from '@/constants/env';
import { AccessModes, AccessModesGroups } from '@/domain/accessMode/accessManagerTypes';
import { OrgSettingsApi } from '@/domain/orgSettings/orgSettingsApi';
import { PermissionsApi } from '@/domain/permissions/permissionsApi';
import { getMySettings } from '@/domain/userSettings/userSettingsApi';
import { LocalFeatureFlags } from '@/features/localFeatureFlags';
import { setAccessMode } from '@/store/accessMode';
import { setOrg } from '@/store/orgSlice';
import { setPermission, setRole, setAdmin, setUserId, setUserPreferences } from '@/store/userSlice';
import type { RootState } from '@/store';
import { useGleap } from '@/tracking/useGleap';
import { useMixpanel } from '@/tracking/useMixpanel';
import { useDatadog } from '@/tracking/useDatadog';
import { AppModule } from '@/appModule';

import '@/locales/i18n';
import '@/styles/App.scss';

const Main = lazy(() => import('./layouts/Main'));

const useAppModule = () => {
  const appModuleRef = useRef(new AppModule());
  const [isLoadedAppModule, setIsLoadedAppModule] = useState(false);

  useEffect(() => {
    const appModule = appModuleRef.current;

    appModule.init().then(() => {
      setIsLoadedAppModule(true);
    });

    return () => {
      appModule.dispose();
    };
  }, []);

  return { isLoadedAppModule };
};

const useFlagsModule = () => {
  const user = useSelector((store: RootState) => store.user);
  const [isLoadedFlagsModule, setIsLoadedFlagsModule] = useState(false);

  const userEmail = user?.email;

  useEffect(() => {
    if (userEmail) {
      (async () => {
        if (NODE_ENV === 'production' && !API_ADDRESS.includes('localhost')) {
          const unleashConfig = {
            appName: 'decorator',
            clientKey: 'decorator',
            environment: DEPLOYMENT_ENV,
            refreshInterval: 600,
            url: `${GITLAB_UNLEASH_CONNECT_SRC}/proxy`,
          };
          await FlagsModule.init({ type: 'UNLEASH', config: unleashConfig });
        } else {
          await FlagsModule.init({ type: 'LOCAL', config: { flags: LocalFeatureFlags } });
        }

        await FlagsModule.update({ userId: userEmail });

        setIsLoadedFlagsModule(true);
      })();
    }
  }, [userEmail]);

  return { isLoadedFlagsModule };
};

const useApp = () => {
  const { isLoadedAppModule } = useAppModule();
  const { isLoadedFlagsModule } = useFlagsModule();

  const dispatch = useDispatch();

  useEffect(() => {
    // Wait for app module to be loaded before making API calls
    if (!isLoadedAppModule) return;

    // get user preferences
    getMySettings().then(res => dispatch(setUserPreferences(res)));

    try {
      // get org
      OrgSettingsApi.getOrg().then(res => dispatch(setOrg(res)));

      // get userId
      OrgSettingsApi.getMe().then(({ userId, role, isAdmin }) => {
        dispatch(setUserId(userId));
        dispatch(setRole(role === 'member' ? 'user' : role));
        dispatch(setAdmin(isAdmin));
      });

      // get permission
      PermissionsApi.getMyLicensePermission().then(permissions => {
        const permission = permissions[0].permission;
        let accesModePermision;
        switch (permission) {
          case 'owner':
            accesModePermision = AccessModes.OWNER;
            break;

          case 'editor':
            accesModePermision = AccessModes.EDITOR;
            break;

          case 'viewer':
          default:
            accesModePermision = AccessModes.VIEWER;
            break;
        }

        dispatch(setPermission(permission));
        dispatch(
          setAccessMode({
            group: AccessModesGroups.USER_PERMISSION,
            accessMode: accesModePermision,
          }),
        );
      });
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, isLoadedAppModule]);

  useMixpanel();
  useGleap();
  useDatadog();

  // TODO: If we do not want to block app loading with feature flags initialization we should consume status from the store with useSelector
  const isLoaded = isLoadedAppModule && isLoadedFlagsModule;

  return { isLoaded };
};

export const App: FC = () => {
  const { isLoaded } = useApp();

  return (
    <>
      <div id="dialog-root" />
      <div id="drag-root" />
      <Suspense fallback={<AppLoadingPage />}>{isLoaded ? <Main /> : <AppLoadingPage />}</Suspense>
      <Toaster />
    </>
  );
};
